import React, { useState } from 'react';
import { 
  TestTube2, Play, Zap, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';

const TestConsoleView = () => {
  const [testing, setTesting] = useState(false);
  const [testConfig, setTestConfig] = useState({
    feature: 'affirmations',
    provider: 'anthropic',
    model: 'claude-3-7-sonnet-20250219',
    system_message: 'You are a compassionate AI therapist.',
    user_message: 'I need encouragement today.',
    variables: {}
  });
  const [testResult, setTestResult] = useState(null);
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  const features = [
    { id: 'affirmations', label: 'Affirmations' },
    { id: 'journaling', label: 'Journaling' },
    { id: 'companion', label: 'Conchi' },
    { id: 'divine_guidance', label: 'Divine Guidance' }
  ];

  const providers = [
    { id: 'anthropic', label: 'Anthropic Claude' },
    { id: 'openai', label: 'OpenAI' },
    { id: 'google', label: 'Google Gemini' }
  ];

  const handleRunTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/test`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testConfig)
      });

      if (!response.ok) throw new Error('Test failed');

      const data = await response.json();
      setTestResult(data.data);

      if (data.data.success) {
        toast({
          title: '✅ Test Successful',
          description: `Completed in ${data.data.latency_ms}ms`,
          variant: 'default'
        });
      } else {
        toast({
          title: '❌ Test Failed',
          description: data.data.error || 'Unknown error',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
          Test Console (Sandbox)
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Test AI outputs safely before going live - no production impact
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Test Configuration</h3>

          <div className="space-y-4">
            {/* Feature Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feature
              </label>
              <select
                value={testConfig.feature}
                onChange={(e) => setTestConfig({ ...testConfig, feature: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {features.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider
              </label>
              <select
                value={testConfig.provider}
                onChange={(e) => setTestConfig({ ...testConfig, provider: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>

            {/* Model Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model Name
              </label>
              <input
                type="text"
                value={testConfig.model}
                onChange={(e) => setTestConfig({ ...testConfig, model: e.target.value })}
                placeholder="claude-3-7-sonnet-20250219"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* System Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Message
              </label>
              <textarea
                value={testConfig.system_message}
                onChange={(e) => setTestConfig({ ...testConfig, system_message: e.target.value })}
                rows={4}
                placeholder="Enter system prompt..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* User Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Input
              </label>
              <textarea
                value={testConfig.user_message}
                onChange={(e) => setTestConfig({ ...testConfig, user_message: e.target.value })}
                rows={3}
                placeholder="Enter test message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Run Button */}
            <Button
              onClick={handleRunTest}
              disabled={testing}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              {testing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Test
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Results Panel */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Test Results</h3>

          {!testResult ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <TestTube2 className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600">No test results yet</p>
              <p className="text-sm text-gray-500 mt-2">Configure and run a test to see results</p>
            </div>
          ) : testResult.success ? (
            <div className="space-y-4">
              {/* Success Badge */}
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Test Passed</span>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-gray-600">Latency</span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {testResult.latency_ms}ms
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <span className="text-xs text-gray-600">Tokens</span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {testResult.total_tokens}
                  </div>
                </div>
              </div>

              {/* Token Breakdown */}
              <div className="text-sm text-gray-600 space-y-1">
                <div>Input: <span className="font-medium">{testResult.input_tokens}</span> tokens</div>
                <div>Output: <span className="font-medium">{testResult.output_tokens}</span> tokens</div>
              </div>

              {/* Response */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Response
                </label>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {testResult.response}
                  </p>
                </div>
              </div>

              {/* System Message Used */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Message Used
                </label>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">
                    {testResult.system_message_used}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Error Badge */}
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-900">Test Failed</span>
              </div>

              {/* Error Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Error Details
                </label>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-800">
                    {testResult.error}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default TestConsoleView;